import {
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@coreai/fluentui-react';
import {
  ArrowLeft20Regular,
  Search20Regular,
} from '@fluentui/react-icons';
import type { ConnectorChoice } from './addConnectionTypes';
import { VirtualizedConnectorList } from './VirtualizedConnectorList';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    gap: tokens.spacingVerticalM,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
  },
  search: {
    flex: 1,
  },
  results: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground2,
  },
});

export interface ConnectorChooserProps {
  connectors: ConnectorChoice[];
  search: string;
  isLoading: boolean;
  error: unknown;
  partialError: unknown;
  onBack: () => void;
  onSearchChange: (value: string) => void;
  onRetry: () => void;
  onSelect: (connector: ConnectorChoice) => void;
}

export const ConnectorChooser = ({
  connectors,
  search,
  isLoading,
  error,
  partialError,
  onBack,
  onSearchChange,
  onRetry,
  onSelect,
}: ConnectorChooserProps) => {
  const styles = useStyles();

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <Button
          appearance="subtle"
          icon={<ArrowLeft20Regular aria-hidden="true" />}
          aria-label="Back to connection types"
          onClick={onBack}
        />
        <Field className={styles.search} label="Search connectors">
          <Input
            type="search"
            value={search}
            onChange={(_, data) => onSearchChange(data.value)}
            contentBefore={<Search20Regular aria-hidden="true" />}
          />
        </Field>
      </div>
      {partialError !== null && partialError !== undefined && (
        <MessageBar intent="warning">
          <MessageBarBody>
            Some connectors could not be loaded. The available results may be incomplete.
          </MessageBarBody>
          <MessageBarActions>
            <Button size="small" onClick={onRetry}>
              Retry
            </Button>
          </MessageBarActions>
        </MessageBar>
      )}
      {isLoading ? (
        <div className={styles.results}>
          <div className={styles.status} role="status">
            <Spinner size="small" />
            <Text>Loading connectors…</Text>
          </div>
        </div>
      ) : error ? (
        <div className={styles.results}>
          <div className={styles.status} role="alert">
            <Text>Could not load connectors.</Text>
            <Button onClick={onRetry}>Retry</Button>
          </div>
        </div>
      ) : connectors.length === 0 ? (
        <div className={styles.results}>
          <div className={styles.status} role="status">
            <Text>
              {search
                ? 'No connectors match your search.'
                : 'No connectors are available.'}
            </Text>
          </div>
        </div>
      ) : (
        <VirtualizedConnectorList
          connectors={connectors}
          onSelect={onSelect}
          ariaLabel="Connectors"
          resetKey={search}
        />
      )}
    </div>
  );
};
